import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './OrderDetails.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function OpenOrderDetails({ warehouse, picker, country, orderType, excludeCarriers, onBack }) {
  const [orders, setOrders] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'gen_date', direction: 'asc' });

  // Local filters
  const [localOrderType, setLocalOrderType] = useState(orderType || 'All');
  const [localExcludeCarriers, setLocalExcludeCarriers] = useState(excludeCarriers || false);
  const [availableOrderTypes, setAvailableOrderTypes] = useState([]);

  // Map order type codes to readable labels
  const orderTypeLabels = {
    'A': 'Assemblies',
    'C': 'Customer',
    'T': 'Transfers'
  };

  useEffect(() => {
    fetchOrderDetails();

    const refreshInterval = setInterval(() => {
      fetchOrderDetails();
    }, 60000); // 60 seconds

    return () => {
      clearInterval(refreshInterval);
    };
  }, [warehouse, picker, country, localOrderType, localExcludeCarriers]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (country && country !== 'All') params.append('country', country);
      if (localOrderType && localOrderType !== 'All') params.append('orderType', localOrderType);
      if (localExcludeCarriers) params.append('excludeCarriers', 'true');

      const response = await axios.get(`${API_URL}/open-orders/picker-orders/${warehouse}/${encodeURIComponent(picker)}?${params}`);
      setOrders(response.data.orders);
      setTotals(response.data.totals);
      setError(null);

      // Extract unique order types from data
      if (response.data.orders) {
        const types = [...new Set(
          response.data.orders.map(o => o.order_type).filter(Boolean)
        )].sort();
        setAvailableOrderTypes(types);
      }
    } catch (err) {
      console.error('Error fetching order details:', err);
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedOrders = [...orders].sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (aValue === null) return 1;
    if (bValue === null) return -1;

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    }

    return sortConfig.direction === 'asc'
      ? String(aValue).localeCompare(String(bValue))
      : String(bValue).localeCompare(String(aValue));
  });

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return ' ⬍';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Parse as local date to avoid timezone issues
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US');
  };

  if (loading) {
    return <div className="order-details-loading">Loading order details...</div>;
  }

  if (error) {
    return <div className="order-details-error">{error}</div>;
  }

  return (
    <div className="order-details-container">
      <div className="order-details-header">
        <button className="back-button" onClick={onBack}>
          ← Back to Picker List
        </button>
        <h2>{picker} - Open Order Details</h2>
        <p className="total-picks">Total Open Picks: {totals?.totalPicks || 0}</p>
        <p className="total-picks">Avg Days Open: {totals?.avgDaysOpen?.toFixed(2) || '0.00'}</p>
      </div>

      <div className="filters-section" style={{ marginBottom: '20px' }}>
        <div className="filter-group">
          <label>Order Type:</label>
          <select value={localOrderType} onChange={(e) => setLocalOrderType(e.target.value)}>
            <option value="All">All</option>
            {availableOrderTypes.map(type => (
              <option key={type} value={type}>
                {orderTypeLabels[type] || type}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group checkbox-group">
          <label>
            <input
              type="checkbox"
              checked={localExcludeCarriers}
              onChange={(e) => setLocalExcludeCarriers(e.target.checked)}
            />
            Exclude Carriers: Z/WC/HFSK
          </label>
        </div>
      </div>

      <div className="order-details-table-container">
        <table className="order-details-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('pick_no')}>
                Pick No{getSortIndicator('pick_no')}
              </th>
              <th onClick={() => handleSort('days_to_pick')}>
                Days Open{getSortIndicator('days_to_pick')}
              </th>
              <th onClick={() => handleSort('line_count')}>
                Lines{getSortIndicator('line_count')}
              </th>
              <th onClick={() => handleSort('qty_count')}>
                Quantity{getSortIndicator('qty_count')}
              </th>
              <th onClick={() => handleSort('weight_count')}>
                Weight{getSortIndicator('weight_count')}
              </th>
              <th onClick={() => handleSort('value_count')}>
                Dollars{getSortIndicator('value_count')}
              </th>
              <th onClick={() => handleSort('carrier')}>
                Carrier{getSortIndicator('carrier')}
              </th>
              <th onClick={() => handleSort('order_type')}>
                Type{getSortIndicator('order_type')}
              </th>
              <th onClick={() => handleSort('gen_date')}>
                Gen Date{getSortIndicator('gen_date')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedOrders.map((order, index) => (
              <tr key={index}>
                <td>{order.pick_no}</td>
                <td>{order.days_to_pick || 0}</td>
                <td>{order.line_count?.toLocaleString() || 0}</td>
                <td>{order.qty_count?.toLocaleString() || 0}</td>
                <td>{Math.round((order.weight_count || 0) / 10000).toLocaleString()} lbs</td>
                <td>${Math.round((order.value_count || 0) / 100).toLocaleString()}</td>
                <td>{order.carrier || ''}</td>
                <td>{order.order_type || ''}</td>
                <td>{formatDate(order.gen_date)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="totals-row">
              <td><strong>Totals:</strong></td>
              <td><strong>Avg: {totals?.avgDaysOpen?.toFixed(2) || '0.00'}</strong></td>
              <td><strong>{(totals?.totalLines || 0).toLocaleString()}</strong></td>
              <td><strong>{(totals?.totalQuantity || 0).toLocaleString()}</strong></td>
              <td><strong>{Math.round((Number(totals?.totalWeight) || 0) / 10000).toLocaleString()} lbs</strong></td>
              <td><strong>${Math.round((Number(totals?.totalDollars) || 0) / 100).toLocaleString()}</strong></td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default OpenOrderDetails;
